import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../apiClient';
import streamClient from '../../streamClient';
import './MeetingKnowledgeSupport.css';

const MeetingKnowledgeSupport = ({ 
  isOpen = false, 
  onClose = () => {}, 
  meetingText = '', 
  transcription = '',
  onUpdateMeetingText = () => {}
}) => {
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showAdjustmentPreview, setShowAdjustmentPreview] = useState(false);
  const [adjustedText, setAdjustedText] = useState('');

  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Example questions specific to meetings
  const exampleQuestions = [
    "Sammanfatta huvudpunkterna från mötet",
    "Vilka beslut togs under mötet?",
    "Vilka åtgärder planerades?",
    "Lägg till information om nästa möte",
    "Förtydliga vad som diskuterades om budget",
    "Ta bort upprepningar från anteckningen"
  ];

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, streamingResponse]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [question]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    const userQuestion = question.trim();
    setQuestion('');
    
    // Add user message to chat
    const userMessage = { role: 'user', content: userQuestion };
    setChatHistory(prev => [...prev, userMessage]);
    
    setIsLoading(true);
    setIsStreaming(true);
    setStreamingResponse('');

    try {
      let fullResponse = '';
      let isAdjustmentRequest = false;
      
      // Check if this is an adjustment request
      const adjustmentKeywords = ['ändra', 'uppdatera', 'lägg till', 'ta bort', 'förtydliga', 'korrigera', 'justera'];
      isAdjustmentRequest = adjustmentKeywords.some(keyword => 
        userQuestion.toLowerCase().includes(keyword)
      );

      if (isAdjustmentRequest) {
        // Handle adjustment request
        await streamClient.post(
          '/api/meeting/adjust',
          {
            meeting_text: meetingText,
            transcription: transcription,
            instruction: userQuestion
          },
          (chunk) => {
            fullResponse += chunk;
            setStreamingResponse(prev => prev + chunk);
          }
        );
        
        // Show adjustment preview
        setAdjustedText(fullResponse);
        setShowAdjustmentPreview(true);
      } else {
        // Handle regular chat
        await streamClient.post(
          '/api/meeting/chat',
          {
            meeting_text: meetingText,
            transcription: transcription,
            question: userQuestion,
            chat_history: chatHistory
          },
          (chunk) => {
            fullResponse += chunk;
            setStreamingResponse(prev => prev + chunk);
          }
        );
      }

      // Add AI response to chat history (only for regular chat)
      if (!isAdjustmentRequest) {
        const aiMessage = { role: 'assistant', content: fullResponse };
        setChatHistory(prev => [...prev, aiMessage]);
      }
      
    } catch (error) {
      console.error('Error in meeting knowledge support:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Tyvärr uppstod ett fel. Försök igen.' 
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingResponse('');
    }
  };

  const handleExampleClick = (exampleQuestion) => {
    setQuestion(exampleQuestion);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleAcceptAdjustment = () => {
    onUpdateMeetingText(adjustedText);
    
    // Add adjustment to chat history
    const adjustmentMessage = { 
      role: 'assistant', 
      content: 'Mötesanteckningen har uppdaterats enligt dina instruktioner.' 
    };
    setChatHistory(prev => [...prev, adjustmentMessage]);
    
    setShowAdjustmentPreview(false);
    setAdjustedText('');
  };

  const handleRejectAdjustment = () => {
    const rejectionMessage = { 
      role: 'assistant', 
      content: 'Justeringen avbröts. Mötesanteckningen är oförändrad.' 
    };
    setChatHistory(prev => [...prev, rejectionMessage]);
    
    setShowAdjustmentPreview(false);
    setAdjustedText('');
  };

  const renderFormattedText = (text) => {
    if (!text || typeof text !== 'string') return null;
    
    const parts = [];
    const boldPattern = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match;
    let iterations = 0;
    const maxIterations = 100;
    
    while ((match = boldPattern.exec(text)) !== null && iterations < maxIterations) {
      iterations++;
      
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(<strong key={`bold-${match.index}-${iterations}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    if (parts.length === 0) {
      return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
    }
    
    return (
      <span style={{ whiteSpace: 'pre-wrap' }}>
        {parts.map((part, index) => (
          <React.Fragment key={index}>{part}</React.Fragment>
        ))}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`meeting-assistant-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`meeting-assistant-sidebar ${isClosing ? 'closing' : ''}`}>
        <div className="meeting-assistant-header">
          <h3>Mötesassistent</h3>
          <button className="close-button" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="meeting-assistant-content">
          <div className="example-questions">
            <h4>Exempel på frågor:</h4>
            <div className="example-buttons">
              {exampleQuestions.map((example, index) => (
                <button
                  key={index}
                  className="example-button"
                  onClick={() => handleExampleClick(example)}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          <div className="chat-container" ref={chatContainerRef}>
            {chatHistory.map((message, index) => (
              <div key={index} className={`chat-message ${message.role}`}>
                <div className="message-content">
                  {renderFormattedText(message.content)}
                </div>
              </div>
            ))}
            
            {isStreaming && streamingResponse && (
              <div className="chat-message assistant streaming">
                <div className="message-content">
                  {renderFormattedText(streamingResponse)}
                  <span className="typing-indicator">●</span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="question-form">
            <textarea
              ref={textareaRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ställ en fråga om mötet eller ge instruktioner för att justera anteckningen..."
              disabled={isLoading}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            <button type="submit" disabled={isLoading || !question.trim()}>
              {isLoading ? '●●●' : 'Skicka'}
            </button>
          </form>
        </div>
      </div>

      {/* Adjustment Preview Modal */}
      {showAdjustmentPreview && (
        <div className="adjustment-preview-overlay">
          <div className="adjustment-preview-modal">
            <div className="preview-header">
              <h2>Förhandsgranska justering</h2>
            </div>
            <div className="preview-content">
              <div className="preview-section">
                <h3>Justerad mötesanteckning:</h3>
                <div className="preview-text">
                  {renderFormattedText(adjustedText)}
                </div>
              </div>
            </div>
            <div className="preview-footer">
              <button className="cancel-btn" onClick={handleRejectAdjustment}>
                Avbryt
              </button>
              <button className="accept-btn" onClick={handleAcceptAdjustment}>
                Acceptera ändringar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingKnowledgeSupport;